<template>
  <basic-dialog :show="show" @close="$emit('close')" maxWidth="700">
    <template v-slot:title>New Search</template>
    <v-row>
      <v-col
        ><v-text-field
          label="Supplier Name/Code Filter"
          v-model="supplierNameFilter"
          :outlined="outlinedInputs"
          hide-details
          clearable
          dense
        ></v-text-field></v-col
      ><v-col
        ><v-text-field
          label="Service Name/Code Filter"
          v-model="serviceNameFilter"
          :outlined="outlinedInputs"
          hide-details
          clearable
          dense
        ></v-text-field
      ></v-col>
    </v-row>
    <v-row justify="center" align="baseline">
      <v-radio-group
        v-if="multiSupplierTypes"
        v-model="selectedSupplierTypes"
        row
        :disabled="$apollo.loading"
        class="ma-0 py-0"
        multiple
        dense
        hide-details
      >
        <v-radio
          v-for="sType in supplierTypes"
          :key="sType"
          :value="sType"
          @click="onSupplierTypeClick(sType)"
          dense
          hide-details
        >
          <template v-slot:label>
            <v-icon left>{{ supplierTypeIcon(sType) }}</v-icon>
          </template>
        </v-radio>
      </v-radio-group>
      <v-radio-group
        v-else
        v-model="selectedSupplierType"
        row
        @change="onServiceTypeChange"
        class="ma-0 py-0"
        dense
        hide-details
      >
        <v-radio
          v-for="sType in supplierTypes"
          :key="sType"
          :value="sType"
          dense
          hide-details
        >
          <template v-slot:label>
            <v-icon left>{{ supplierTypeIcon(sType) }}</v-icon>
          </template>
        </v-radio>
      </v-radio-group>
    </v-row>
    <v-row>
      <v-col>
        <dates-range-picker
          :gtDateFromString="startDate"
          :gtDateToString="endDate"
          :rangeStart="dateRangeStart"
          :rangeEnd="dateRangeEnd"
          @onRangeSelected="onDateRangeSelected"
          :outlined="outlinedInputs"
          dense
        ></dates-range-picker>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          :outlined="outlinedInputs"
          dense
          hide-details
          label="Adults"
          type="number"
          v-model="adults"
        />
      </v-col>
      <v-col>
        <v-text-field
          :outlined="outlinedInputs"
          dense
          hide-details
          label="Children"
          type="number"
          v-model="children"
        />
      </v-col>
      <v-col>
        <v-text-field
          :outlined="outlinedInputs"
          dense
          hide-details
          label="Infants"
          type="number"
          v-model="infants"
        />
      </v-col>
    </v-row>
    <v-row
      ><v-col>
        <destination-list-select
          v-model="destinationId"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></destination-list-select> </v-col
      ><v-col>
        <provider-list-select
          v-model="providers"
          :outlined="outlinedInputs"
          dense
          hideDetails
        />
      </v-col>
    </v-row>
    <v-row v-if="showPickupDropoffForThompsons">
      <v-col>
        <thompsons-place-list-select
          v-model="customParams.pickupPoint"
          :outlined="outlinedInputs"
          dense
          hide-details
          label="Pickup Point"
          :destination-id="destinationId"
        />
      </v-col>
      <v-col>
        <thompsons-place-list-select
          v-model="customParams.dropoffPoint"
          :outlined="outlinedInputs"
          dense
          hide-details
          label="Dropoff Point"
          :destination-id="destinationId"
        />
      </v-col>
    </v-row>
    <v-row v-if="showAzamaraFields">
      <v-col cols="6">
        <v-select
          v-model="azamaraShipCode"
          :items="azamaraShips"
          item-text="name"
          item-value="code"
          label="Ship (Azamara)"
          :outlined="outlinedInputs"
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="azamaraMinNights"
          label="Min Nights"
          type="number"
          min="1"
          :outlined="outlinedInputs"
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="azamaraMaxNights"
          label="Max Nights"
          type="number"
          min="1"
          :outlined="outlinedInputs"
          dense
          hide-details
          clearable
        />
      </v-col>
    </v-row>
    <v-alert
      v-if="hotelbedsHint"
      type="info"
      text
      dense
      class="mt-3 mb-0"
      >{{ hotelbedsHint }}</v-alert
    >
    <v-row align="center">
      <v-col>
        <v-switch
          v-model="multiSupplierTypes"
          label="Multi"
          class="my-0 py-0"
          @change="onMultiSFRChange"
          hide-details
          dense
        ></v-switch>
      </v-col>
      <v-col>
        <v-switch
          v-model="includeUnpriced"
          label="Include Unpriced"
          class="ml-2 my-0 py-0"
          hide-details
          dense
        ></v-switch>
      </v-col>
    </v-row>
    <template v-slot:actions>
      <v-btn color="primary" @click="onStart" dense class="ma-0"
        ><v-icon left>mdi-magnify</v-icon>Start Search</v-btn
      >
    </template>
    <v-row v-if="debug">
      <v-col>
        <code>{{ customParams }}</code>
        <code>{{ providers }}</code>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../../shared/BasicDialog.vue";
import DatesRangePicker from "../../../shared/DatesRangePicker.vue";
import DestinationListSelect from "../../../shared/DestinationListSelect.vue";
import ProviderListSelect from "../../../shared/ProviderListSelect.vue";
import { SUPPLIER_TYPES } from "../../../../../constants/supplierTypes";
import { SupplierTypeIconMixin } from "../../../../../mixins/SupplierTypeIconMixin";
import ThompsonsPlaceListSelect from "../../../shared/ThompsonsPlaceListSelect.vue";

// #11473 SeaWare ship codes
const AZAMARA_SHIPS = [
  { code: "JR", name: "Azamara Journey" },
  { code: "ON", name: "Azamara Onward" },
  { code: "PR", name: "Azamara Pursuit" },
  { code: "QS", name: "Azamara Quest" },
];

// Providers that only return one service type: picking them switches the search to it.
// #11473 Azamara only returns cruises (PACKAGE), #11460 HotelBeds only hotels for now.
const PROVIDER_SERVICE_TYPES = {
  AZAMARA: "PACKAGE",
  HOTELBEDS: "ACCOMMODATION",
};

// customParams comes back from the server as a JSON string
function parseCustomParams(value) {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value) || {};
    } catch (e) {
      return {};
    }
  }
  return { ...value };
}

export default {
  name: "rate-search-params-dialog",

  components: {
    BasicDialog,
    DatesRangePicker,
    DestinationListSelect,
    ProviderListSelect,
    ThompsonsPlaceListSelect,
  },

  mixins: [SupplierTypeIconMixin],

  props: {
    value: {
      type: Object,
      default: null,
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    const customParams = parseCustomParams(this.value && this.value.customParams);
    return {
      debug: false,
      supplierNameFilter: this.value ? this.value.supplierNameFilter : "",
      serviceNameFilter: this.value ? this.value.serviceNameFilter : "",
      startDate: this.value ? this.value.startDate : "",
      dateRangeStart: this.value ? this.value.startDate : "",
      dateRangeEnd: this.value ? this.value.endDate : "",
      endDate: this.value ? this.value.endDate : "",
      adults: this.value ? this.value.adults : 2,
      children: this.value ? this.value.children : 0,
      infants: this.value ? this.value.infants : 2,
      destinationId: this.value ? this.value.destinationId : "",
      customParams: customParams,
      azamaraShips: AZAMARA_SHIPS,
      azamaraShipCode: customParams.shipCode || null,
      azamaraMinNights: customParams.minNights || null,
      azamaraMaxNights: customParams.maxNights || null,

      selectedSupplierType: this.supplierType
        ? this.supplierType
        : "ACCOMMODATION",
      providers:
        this.value && this.value.providers
          ? [...this.value.providers]
          : this.$store.getters.localSettings("providerListSelectDefault", ["GTWEB"]),
      selectedSupplierTypes:
        this.value && this.value.serviceTypes
          ? [...this.value.serviceTypes]
          : ["ACCOMMODATION"],
      multiSupplierTypes: this.$store.getters.localSettings("multiSFR", false),
      supplierTypes: SUPPLIER_TYPES,
      includeUnpriced: false,
    };
  },

  computed: {
    showPickupDropoffForThompsons() {
      return (
        this.providers.includes("THOMPSONS_AFRICA") &&
        this.selectedSupplierTypes.includes("TRANSPORT")
      );
    },
    showAzamaraFields() {
      return this.providers.includes("AZAMARA");
    },
    // #11460 HotelBeds searches hotels only; without a destination it asks for every
    // approved hotel on the account
    hotelbedsHint() {
      if (!this.providers.includes("HOTELBEDS")) return "";
      if (!this.selectedSupplierTypes.includes("ACCOMMODATION")) {
        return "HotelBeds only returns accommodation; other service types are not searched with HotelBeds.";
      }
      if (!this.destinationId) {
        return "HotelBeds: pick a destination to keep the search fast. Only hotels approved for HotelBeds are searched.";
      }
      return "";
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
  },

  methods: {
    onDateRangeSelected(payload) {
      this.startDate = payload.dateFrom;
      this.endDate = payload.dateTo;
    },
    onSupplierTypeClick(sType) {
      if (this.selectedSupplierTypes.indexOf(sType) > -1) {
        this.selectedSupplierTypes = [
          ...this.selectedSupplierTypes.filter((t) => t != sType),
        ];
      } else {
        this.selectedSupplierTypes.push(sType);
      }
    },
    onServiceTypeChange(payload) {
      //console.log("onServiceTypeChange: ", payload);
      this.selectedSupplierTypes = [payload];
    },
    onMultiSFRChange() {
      this.$store.dispatch("updateLocalSettings", {
        multiSFR: this.multiSupplierTypes,
      });
    },
    buildCustomParams() {
      const params = { ...this.customParams };
      delete params.shipCode;
      delete params.minNights;
      delete params.maxNights;
      if (this.showAzamaraFields) {
        if (this.azamaraShipCode) params.shipCode = this.azamaraShipCode;
        const min = parseInt(this.azamaraMinNights);
        const max = parseInt(this.azamaraMaxNights);
        if (min > 0) params.minNights = min;
        if (max > 0) params.maxNights = max;
      }
      return params;
    },
    onStart() {
      //console.log("starting new search");
      this.$emit("input", {
        id: this.value && this.value.id ? this.value.id : undefined,
        startDate: this.startDate,
        endDate: this.endDate,
        serviceTypes: this.selectedSupplierTypes,
        destinationId: this.destinationId,
        serviceName: this.serviceNameFilter,
        supplierName: this.supplierNameFilter,
        providers: this.providers,
        includeUnpriced: this.includeUnpriced,
        multiSFR: this.multiSFR,
        adults: parseInt(this.adults),
        children: parseInt(this.children),
        infants: parseInt(this.infants),
        customParams: JSON.stringify(this.buildCustomParams()),
      });
    },
  },

  watch: {
    value(newVal) {
      this.startDate = newVal.startDate;
      this.endDate = newVal.endDate;
      this.dateRangeStart = newVal.startDate;
      this.dateRangeEnd = newVal.endDate;
      this.serviceNameFilter = newVal.serviceName;
      this.supplierNameFilter = newVal.supplierName;
      this.adults = newVal.adults;
      this.children = newVal.children;
      this.infants = newVal.infants;
      this.destinationId = newVal.destinationId;
      this.providers = newVal.providers;
      this.selectedSupplierTypes = [...newVal.serviceTypes];
      this.selectedSupplierType = newVal.serviceTypes[0];
      this.customParams = parseCustomParams(newVal.customParams);
      this.azamaraShipCode = this.customParams.shipCode || null;
      this.azamaraMinNights = this.customParams.minNights || null;
      this.azamaraMaxNights = this.customParams.maxNights || null;
    },
    providers(newVal, oldVal) {
      const added = (newVal || []).filter(
        (p) => PROVIDER_SERVICE_TYPES[p] && !(oldVal || []).includes(p)
      );
      added.forEach((provider) => {
        const sType = PROVIDER_SERVICE_TYPES[provider];
        if (this.selectedSupplierTypes.includes(sType)) return;
        if (this.multiSupplierTypes) {
          this.selectedSupplierTypes.push(sType);
        } else {
          this.selectedSupplierType = sType;
          this.selectedSupplierTypes = [sType];
        }
      });
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/rates-search/RateSearchParamsDialog.vue